import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiInfo, FiPaperclip, FiFileText, FiChevronRight, FiEdit3, FiCheckCircle } from 'react-icons/fi';
import { AiOutlineTrophy } from 'react-icons/ai';
import { t } from 'i18next';
import { IconVerified } from '@/components/custom/icon';
import { Button } from '@/components/ui/button';
import { Badge, BadgeGroup } from '@/components/ui/badge';
import { ModuleCard, ModuleCardAlert, ModuleCardHeader, ModuleCardHeaderContent, ModuleCardText, ModuleCardTitle } from '@/components/custom/module';
import { MedalContainer } from '@/components/custom/medal';
import { cn } from '@/lib/utils';
import { PATHS } from '@/routes/paths';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Loader } from '@/components/custom/loader';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';

export function CourseMonitoringModuleCard(props) {
  const navigate = useNavigate();
  const playfulStudent = usePlayfulStudent();
  const { module, courseId, canAccess, index = 0, filesCount = 0, activitiesCount = 0, bookletsCount = 0, details, isLoadingDetails = false, onRequestDetails = () => {} } = props;
  const [showFiles, setShowFiles] = useState(false);
  const [showActivities, setShowActivities] = useState(false);
  const [showBooklets, setShowBooklets] = useState(false);

  // O detalhamento (lista de arquivos/atividades/apostilas) só é buscado no
  // clique de algum dos botões; até chegar, o modal mostra um loading.
  const files = details?.files ?? [];
  const activities = details?.activities ?? [];
  const booklets = details?.booklets ?? [];
  const isLoadingModuleDetails = isLoadingDetails || !details;

  const getModuleStatus = module => {
    const now = new Date();
    let badgeVariant = '';
    let badgeText = '';
    let disclaimer = false;

    if (module?.learning_system_users?.length === 0) {
      badgeVariant = 'warning';
      badgeText = 'Novo';
    }
    if (module?.progress === 100 && now >= new Date(module?.finished_at)) {
      badgeVariant = 'success';
      badgeText = 'Concluído';
    }
    if (module?.progress === 100 && now < new Date(module?.finished_at)) {
      badgeVariant = 'warning';
      badgeText = 'Em andamento';
      disclaimer = true;
    }
    if (module?.progress < 100 && module?.progress > 0) {
      badgeVariant = 'warning';
      badgeText = 'Em andamento';
    }

    return { badgeVariant, badgeText, disclaimer };
  };

  const { badgeVariant, badgeText, disclaimer } = getModuleStatus(module);

  // Linha de módulo lúdica: título + badge, progresso e ação numa lista.
  if (playfulStudent) {
    const finishedCount = module?.learning_system_items_finished_count ?? 0;
    const totalCount = module?.learning_system_items_total_count ?? 0;
    const percent = totalCount ? Math.round((finishedCount / totalCount) * 100) : 0;
    const playfulBadgeVariant = badgeVariant === 'success' ? 'success-soft' : 'amber-soft';

    return (
      <>
        {/* Sem a tela "sobre" no fluxo infantil: módulo abre direto na sala,
            que se encarrega de criar a matrícula quando não existe. */}
        <article
          onClick={() => navigate(`/show-course/${module.id}/classroom`)}
          className="flex cursor-pointer flex-wrap items-center gap-4 rounded-[16px] border border-neutral-100 bg-white p-4 shadow-card transition-[box-shadow,transform] duration-150 hover:-translate-y-0.5 hover:shadow-card-hover"
          style={{ animation: `trilha-in 380ms cubic-bezier(0.23, 1, 0.32, 1) ${250 + index * 70}ms both` }}
        >
          <div className="min-w-[200px] flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3
                className="mb-0 text-base font-extrabold leading-snug"
                style={{ color: '#222731' }}
              >
                {module?.title}
              </h3>
              {badgeText && (
                <Badge
                  variant={playfulBadgeVariant}
                  className="shrink-0 rounded-full px-2.5 py-1 text-xs font-bold"
                >
                  {badgeText}
                </Badge>
              )}
            </div>

            <div className="mt-2 flex items-center gap-3">
              <div className="h-2 w-full max-w-[220px] overflow-hidden rounded-full bg-[var(--color-bg-lilac)]">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${Math.min(percent, 100)}%`, background: 'var(--gradient-progress-bar)' }}
                />
              </div>
              <span className="whitespace-nowrap text-xs font-bold text-neutral-500">
                {finishedCount}/{totalCount} {t('lms.lms')}
              </span>
            </div>

            {disclaimer && <p className="mb-0 mt-2 text-xs font-semibold leading-relaxed text-neutral-500">Parabéns, todas as aulas foram concluídas! Fica de olho: mais conteúdos podem chegar em breve. 👀</p>}
          </div>

          {filesCount > 0 && (
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                onRequestDetails();
                setShowFiles(true);
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-full border-2 border-[var(--color-bg-lilac)] bg-white px-4 py-2 text-sm font-bold text-[var(--color-secondary)] transition-[border-color,transform] duration-100 hover:border-[var(--color-secondary)] active:scale-[0.97]"
            >
              <FiPaperclip className="h-4 w-4" /> Arquivos
              <span
                className="flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-extrabold text-white"
                style={{ background: 'var(--color-secondary)' }}
              >
                {filesCount}
              </span>
            </button>
          )}

          {activitiesCount > 0 && (
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                onRequestDetails();
                setShowActivities(true);
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-full border-2 border-[var(--color-bg-lilac)] bg-white px-4 py-2 text-sm font-bold text-[var(--color-secondary)] transition-[border-color,transform] duration-100 hover:border-[var(--color-secondary)] active:scale-[0.97]"
            >
              <FiCheckCircle className="h-4 w-4" /> Atividades
              <span
                className="flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-extrabold text-white"
                style={{ background: 'var(--color-secondary)' }}
              >
                {activitiesCount}
              </span>
            </button>
          )}

          {bookletsCount > 0 && (
            <button
              type="button"
              onClick={event => {
                event.stopPropagation();
                onRequestDetails();
                setShowBooklets(true);
              }}
              className="flex shrink-0 items-center gap-1.5 rounded-full border-2 border-[var(--color-bg-lilac)] bg-white px-4 py-2 text-sm font-bold text-[var(--color-secondary)] transition-[border-color,transform] duration-100 hover:border-[var(--color-secondary)] active:scale-[0.97]"
            >
              <FiEdit3 className="h-4 w-4" /> Exercícios
              <span
                className="flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs font-extrabold text-white"
                style={{ background: 'var(--color-secondary)' }}
              >
                {bookletsCount}
              </span>
            </button>
          )}

          {canAccess ? (
            <span
              className="relative shrink-0 overflow-hidden rounded-full px-8 py-2.5 text-center text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
              style={{ background: 'var(--gradient-btn-start)', boxShadow: '0 4px 0 var(--color-secondary-dark), 0 6px 12px rgba(68,43,140,0.35)' }}
            >
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.18) 0%, transparent 100%)' }}
              />
              <span className="relative">Acessar</span>
            </span>
          ) : (
            <span className="shrink-0 cursor-not-allowed rounded-full border border-neutral-200 bg-neutral-100 px-6 py-2.5 text-center text-sm font-bold text-neutral-400">Conclua o módulo anterior</span>
          )}
        </article>

        {/* Modal com os arquivos do módulo. Fica fora do <article> para o clique
          não borbulhar pro onClick de navegação do card. */}
        {filesCount > 0 && (
          <Dialog
            open={showFiles}
            onOpenChange={setShowFiles}
          >
            <DialogContent
              size="sm"
              hideCloseButton
              className="w-[420px] gap-0 rounded-[24px] border-0 p-0 sm:rounded-[24px]"
            >
              <div
                className="rounded-t-[24px] px-6 pb-4 pt-6"
                style={{ background: 'var(--color-bg-lilac)' }}
              >
                <DialogTitle
                  className="mb-1 text-lg font-extrabold"
                  style={{ color: '#222731' }}
                >
                  📎 Arquivos do módulo
                </DialogTitle>
                <DialogDescription className="mb-0 text-sm font-semibold text-neutral-500">{module?.title}</DialogDescription>
              </div>

              <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto p-5">
                {isLoadingModuleDetails ? (
                  <Loader />
                ) : (
                  files.map(file => (
                    <Link
                      key={file.id}
                      to={PATHS.MATERIALS.DETAIL(file.id, module.id)}
                      className="group flex items-center gap-3 rounded-[12px] border border-neutral-100 p-3 transition-colors duration-150 hover:border-neutral-200 hover:bg-neutral-50"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-lilac)]">
                        <FiFileText className="h-4 w-4 text-[var(--color-secondary)]" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className="block truncate text-sm font-bold"
                          style={{ color: '#222731' }}
                        >
                          {file.title}
                        </span>
                        {file['file-size'] && <span className="text-xs font-semibold text-neutral-400">{file['file-size']}</span>}
                      </span>
                      <FiChevronRight className="h-4 w-4 shrink-0 text-neutral-300 transition-transform duration-150 group-hover:translate-x-0.5" />
                    </Link>
                  ))
                )}
              </div>

              <div className="px-5 pb-5">
                <button
                  type="button"
                  onClick={() => setShowFiles(false)}
                  className="w-full rounded-full py-2.5 text-sm font-bold text-neutral-400 transition-colors duration-150 hover:text-neutral-600"
                >
                  Fechar
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Modal com as atividades do módulo. */}
        {activitiesCount > 0 && (
          <Dialog
            open={showActivities}
            onOpenChange={setShowActivities}
          >
            <DialogContent
              size="sm"
              hideCloseButton
              className="w-[420px] gap-0 rounded-[24px] border-0 p-0 sm:rounded-[24px]"
            >
              <div
                className="rounded-t-[24px] px-6 pb-4 pt-6"
                style={{ background: 'var(--color-bg-lilac)' }}
              >
                <DialogTitle
                  className="mb-1 text-lg font-extrabold"
                  style={{ color: '#222731' }}
                >
                  📝 Atividades do módulo
                </DialogTitle>
                <DialogDescription className="mb-0 text-sm font-semibold text-neutral-500">{module?.title}</DialogDescription>
              </div>

              <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto p-5">
                {isLoadingModuleDetails ? (
                  <Loader />
                ) : (
                  activities.map(activity => (
                    <Link
                      key={activity.id}
                      to={PATHS.COMPLEMENTARY_ACTIVITY.DETAIL(activity.id)}
                      className="group flex items-center gap-3 rounded-[12px] border border-neutral-100 p-3 transition-colors duration-150 hover:border-neutral-200 hover:bg-neutral-50"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-lilac)]">
                        <FiCheckCircle className="h-4 w-4 text-[var(--color-secondary)]" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className="block truncate text-sm font-bold"
                          style={{ color: '#222731' }}
                        >
                          {activity.title}
                        </span>
                        {activity?.status && <span className="text-xs font-semibold text-neutral-400">{activity.status}</span>}
                      </span>
                      <FiChevronRight className="h-4 w-4 shrink-0 text-neutral-300 transition-transform duration-150 group-hover:translate-x-0.5" />
                    </Link>
                  ))
                )}
              </div>

              <div className="px-5 pb-5">
                <button
                  type="button"
                  onClick={() => setShowActivities(false)}
                  className="w-full rounded-full py-2.5 text-sm font-bold text-neutral-400 transition-colors duration-150 hover:text-neutral-600"
                >
                  Fechar
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Modal com as apostilas do módulo. */}
        {bookletsCount > 0 && (
          <Dialog
            open={showBooklets}
            onOpenChange={setShowBooklets}
          >
            <DialogContent
              size="sm"
              hideCloseButton
              className="w-[420px] gap-0 rounded-[24px] border-0 p-0 sm:rounded-[24px]"
            >
              <div
                className="rounded-t-[24px] px-6 pb-4 pt-6"
                style={{ background: 'var(--color-bg-lilac)' }}
              >
                <DialogTitle
                  className="mb-1 text-lg font-extrabold"
                  style={{ color: '#222731' }}
                >
                  📚 Exercícios do módulo
                </DialogTitle>
                <DialogDescription className="mb-0 text-sm font-semibold text-neutral-500">{module?.title}</DialogDescription>
              </div>

              <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto p-5">
                {isLoadingModuleDetails ? (
                  <Loader />
                ) : (
                  booklets.map(booklet => (
                    <Link
                      key={booklet.id}
                      to={PATHS.QUESTIONS.TAB_DETAIL('apostilas', booklet?.['question-book']?.id)}
                      className="group flex items-center gap-3 rounded-[12px] border border-neutral-100 p-3 transition-colors duration-150 hover:border-neutral-200 hover:bg-neutral-50"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-bg-lilac)]">
                        <FiEdit3 className="h-4 w-4 text-[var(--color-secondary)]" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className="block truncate text-sm font-bold"
                          style={{ color: '#222731' }}
                        >
                          {booklet?.['question-book']?.title}
                        </span>
                        <span className="text-xs font-semibold text-neutral-400">{booklet?.['question-book']?.['question-count'] ?? 0} questões</span>
                      </span>
                      <FiChevronRight className="h-4 w-4 shrink-0 text-neutral-300 transition-transform duration-150 group-hover:translate-x-0.5" />
                    </Link>
                  ))
                )}
              </div>

              <div className="px-5 pb-5">
                <button
                  type="button"
                  onClick={() => setShowBooklets(false)}
                  className="w-full rounded-full py-2.5 text-sm font-bold text-neutral-400 transition-colors duration-150 hover:text-neutral-600"
                >
                  Fechar
                </button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }

  return (
    <ModuleCard onClick={() => navigate(`/curso/${courseId}/modulo/${module.id}/sobre`)}>
      <ModuleCardHeader className="w-[350px]">
        <ModuleCardHeaderContent>
          <div className="flex items-start gap-1">
            {module?.['official_content'] && <IconVerified />}
            <ModuleCardTitle title={module?.title}>{module?.title}</ModuleCardTitle>
          </div>
        </ModuleCardHeaderContent>
      </ModuleCardHeader>

      {disclaimer ? (
        <ModuleCardAlert>
          <FiInfo
            data-tooltip-content={`O certificado do módulo estará disponível a partir de ${new Date(module?.finished_at).toLocaleDateString('pt-br')}`}
            size={16}
          />
          <span>
            Parabéns, todas as aulas foram concluídas. <b>Mantenha-se atento, mais conteudos podem chegar em breve.</b>
          </span>
        </ModuleCardAlert>
      ) : (
        <BadgeGroup>
          {badgeText && (
            <Badge
              variant={badgeVariant}
              size="xs"
              className="cursor-pointer"
            >
              {badgeText}
            </Badge>
          )}
        </BadgeGroup>
      )}

      <ModuleCardText>
        {module?.progress === 0 ? <strong>0</strong> : <strong>{module?.learning_system_items_finished_count}</strong>}/{module?.learning_system_items_total_count} {t('lms.lms')}
      </ModuleCardText>

      {module?.progress === 100 && (
        <MedalContainer
          size="sm"
          src="more"
          value={<AiOutlineTrophy />}
        />
      )}

      <Button
        variant="outline"
        wide
        disabled={!canAccess}
        className="hidden sm:flex"
      >
        Acessar
      </Button>

      {/* <ModuleCardFooter>
        {newModule ? (
          <Button
            variant="outline"
            wide
            fullWidth
            className="hidden sm:flex"
          >
            Visualizar
          </Button>
        ) : (
          <>
            <ModuleCardText>{t('lms.buttonContinue')}</ModuleCardText>
            <ModuleCardLink to={`/show-course/${374}/classroom`}>
              <CardIcon>
                <FiPlayCircle size={16} />
              </CardIcon>
              <ModuleCardLinkText title={'Aula 2 - Física clássica'}>Aula 2 - Física clássica</ModuleCardLinkText>
            </ModuleCardLink>
          </>
        )}
      </ModuleCardFooter> */}
    </ModuleCard>
  );
}
